import { StandardEvents } from '@shopify/events';

export function orderDateInLosAngeles(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', calendar: 'gregory',
    year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
  return new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day)));
}

export function addBusinessDays(date, days) {
  const result = new Date(date);
  for (let counted = 0; counted < days;) {
    result.setUTCDate(result.getUTCDate() + 1);
    if (result.getUTCDay() !== 0 && result.getUTCDay() !== 6) counted++;
  }
  return result;
}

export function deliveryWindows(now = new Date()) {
  const orderDate = orderDateInLosAngeles(now);
  const start = new Date(orderDate);
  start.setUTCDate(start.getUTCDate() + 7);
  const dispatch = addBusinessDays(orderDate, 3);
  return { orderDate, start, dispatch, standard: addBusinessDays(dispatch, 9), express: addBusinessDays(dispatch, 6) };
}

export function formatDeliveryWindow(start, end, locale = 'en-US') {
  const month = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  if (start.getUTCFullYear() !== end.getUTCFullYear()) {
    return `${start.getUTCDate()} ${month.format(start)} ${start.getUTCFullYear()}–${end.getUTCDate()} ${month.format(end)} ${end.getUTCFullYear()}`;
  }
  if (start.getUTCMonth() !== end.getUTCMonth()) {
    return `${start.getUTCDate()} ${month.format(start)}–${end.getUTCDate()} ${month.format(end)}`;
  }
  return `${start.getUTCDate()}–${end.getUTCDate()} ${month.format(end)}`;
}

if (typeof HTMLElement !== 'undefined' && !customElements.get('aware-delivery-estimate')) {
  class AwareDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.generation = 0;
      this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
      this.section?.addEventListener('change', this.onChange);
      document.addEventListener('visibilitychange', this.refresh);
      window.addEventListener('pageshow', this.refresh);
      this.timer = window.setInterval(this.refresh, 60000);
      this.refresh();
    }

    disconnectedCallback() {
      this.section?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
      this.section?.removeEventListener('change', this.onChange);
      document.removeEventListener('visibilitychange', this.refresh);
      window.removeEventListener('pageshow', this.refresh);
      window.clearInterval(this.timer);
    }

    onChange = () => this.refresh();

    onProductSelect = async event => {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      const generation = ++this.generation;
      this.querySelector('[data-delivery-window]')?.setAttribute('hidden', '');
      try {
        const { detail } = await event.promise;
        if (!this.isConnected || generation !== this.generation || !detail?.html) return;
        if (!detail.newProduct && detail.productId && String(detail.productId) !== this.dataset.productId) return;
        const updated = detail.html.querySelector(`aware-delivery-estimate[data-block-id="${this.dataset.blockId}"]`);
        if (!updated) return;
        this.dataset.productId = updated.dataset.productId;
        this.dataset.deliveryState = updated.dataset.deliveryState;
        this.replaceChildren(...Array.from(updated.childNodes).map(node => node.cloneNode(true)));
        this.refresh();
      } catch {
        if (generation === this.generation) this.refresh();
      }
    };

    refresh = () => {
      const pearlSelection = this.section?.querySelector('.aware-pearl-selection input:checked');
      const state = pearlSelection?.value === "I'd like to choose my pearl" ? 'pearl' : this.dataset.deliveryState;
      const regular = state === 'regular';
      this.querySelector('[data-delivery-window]').hidden = !regular;
      this.querySelector('[data-delivery-exception]').hidden = regular;
      for (const message of this.querySelectorAll('[data-delivery-message]')) {
        message.hidden = message.dataset.deliveryMessage !== state;
      }
      if (!regular) return;
      const windows = deliveryWindows();
      const locale = document.documentElement.lang || 'en-US';
      this.querySelector('[data-delivery-standard]').textContent = formatDeliveryWindow(windows.start, windows.standard, locale);
    };
  }
  customElements.define('aware-delivery-estimate', AwareDeliveryEstimate);
}

